import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TranslationProvider } from './context/TranslationContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { API_ENDPOINTS } from './utils/config';
import { useTranslation } from './context/TranslationContext';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import GeneralTerms from './pages/GeneralTerms';
import Header from './components/Header';
import StatsCards from './components/StatsCards';
import YearlyOverview from './components/YearlyOverview';
import SavingsChart from './components/SavingsChart';
import ProcurementSection from './components/ProcurementSection';
import AdminTable from './components/AdminTable';
import SentEmailsDashboard from './components/SentEmailsDashboard';
import RequestDetailPage from './components/RequestDetailPage';
import NotificationModal from './components/NotificationModal';
import NewRequestModal from './components/NewRequestModal';
import TokenExpiredModal from './components/TokenExpiredModal';
import ReportIssueButton from './components/ReportIssueButton';
import Footer from './components/Footer';
import InvoicePage from './components/InvoicePage';
import InvoiceVerificationPage from './components/InvoiceVerificationPage';
import { useApiInterceptor, setupGlobalFetchInterceptor } from './hooks/useApiInterceptor';
import './App.css';

function AppContent() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [userType, setUserType] = useState<'user' | 'admin' | null>(null);
  const [currentView, setCurrentView] = useState<'main' | 'sent-emails'>('main');
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionRequestId, setRevisionRequestId] = useState<string | null>(null);
  const [revisionRequestData, setRevisionRequestData] = useState<any>(null);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [showLoginPage, setShowLoginPage] = useState(false);
  const [showTokenExpiredModal, setShowTokenExpiredModal] = useState(false);

  // Update userType when user changes
  React.useEffect(() => {
    if (user) {
      // Check both role and is_admin field for admin access
      const isAdmin = user.role === 'admin' || user.is_admin === true;
      const newUserType = isAdmin ? 'admin' : 'user';
      setUserType(newUserType);
    } else {
      setUserType(null);
    }
  }, [user]);

  // Setup token expired callback
  const handleTokenExpired = () => {
    console.log('🔒 Token expired callback triggered');
    // Otomatik logout yap
    logout();
    setUserType(null);
    setCurrentView('main');
    setShowLandingPage(true);
    setShowLoginPage(false);
    setShowTokenExpiredModal(true);
  };

  // Setup token expired callback - temporarily disabled
  // useApiInterceptor(handleTokenExpired);

  // useEffect(() => {
  //   // Setup global fetch interceptor
  //   setupGlobalFetchInterceptor();
  // }, []);

  const handleLogin = (type: 'user' | 'admin') => {
    setUserType(type);
    // Navigate to appropriate page after login
    if (type === 'admin') {
      navigate('/admin-page');
    } else {
      navigate('/user-page');
    }
  };

  const handleLandingPageLogin = () => {
    navigate('/signin');
  };

  const handleLogout = () => {
    logout();
    setUserType(null);
    setCurrentView('main');
    setShowLandingPage(true);
    setShowLoginPage(false);
    setShowTokenExpiredModal(false);
    navigate('/');
  };

  const handleTokenExpiredLogin = () => {
    setShowTokenExpiredModal(false);
    navigate('/signin');
  };

  const handleSentEmailsClick = () => {
    setCurrentView('sent-emails');
  };

  const handleNotificationsClick = () => {
    setShowNotificationModal(true);
  };

  const handleRevisionClick = async (requestId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        alert(t('common.authentication_required'));
        return;
      }

      // Fetch the request data
      const response = await fetch(`${API_ENDPOINTS.REQUEST_DETAIL(requestId)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setRevisionRequestData(data.data);
        setRevisionRequestId(requestId);
        setShowRevisionModal(true);
      } else {
        alert(t('common.failed_fetch_requests'));
      }
    } catch (error) {
      console.error('Error fetching request data:', error);
      alert(t('common.failed_fetch_requests'));
    }
  };

  const handleBackToMain = () => {
    setCurrentView('main');
  };

  const handleNewRequestClick = () => {
    setShowNewRequestModal(true);
  };

  const handleCloseNewRequestModal = () => {
    setShowNewRequestModal(false);
  };

  const handleSubmitNewRequest = async (formData: any) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        alert(t('common.authentication_required'));
        return;
      }

      const response = await fetch(API_ENDPOINTS.REQUESTS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        alert(t('common.request_created_successfully'));
        handleCloseNewRequestModal();
        // Optionally refresh the page or update the data
        window.location.reload();
      } else {
        alert(data.message || t('common.failed_create_request'));
      }
    } catch (error) {
      console.error('Error creating request:', error);
      alert(t('common.failed_create_request'));
    }
  };

  const handleRevisionModalClose = () => {
    setShowRevisionModal(false);
    setRevisionRequestId(null);
    setRevisionRequestData(null);
  };

  const handleRevisionSubmit = async (formData: any) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token || !revisionRequestId) {
        alert(t('common.request_id_missing'));
        return;
      }

      const response = await fetch(API_ENDPOINTS.UPDATE_REVISION(revisionRequestId), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        alert(t('common.request_updated_successfully'));
        handleRevisionModalClose();
        // Refresh notifications
        setShowNotificationModal(true);
      } else {
        alert(data.message || t('common.failed_update_request'));
      }
    } catch (error) {
      console.error('Error updating request:', error);
      alert(t('common.failed_update_request'));
    }
  };

  if (isLoading) {
    return <div>{t('common.loading')}</div>;
  }

  // Handle routing based on URL and authentication status
  if (!isAuthenticated) {
    if (location.pathname === '/signin') {
      return <LoginPage onLogin={handleLogin} onBack={() => navigate('/')} />;
    }
    return <LandingPage onLogin={handleLandingPageLogin} />;
  }

  // If authenticated, redirect to appropriate page if on wrong URL
  if (isAuthenticated && userType) {
    if (location.pathname === '/signin') {
      // Redirect to appropriate dashboard
      if (userType === 'admin') {
        navigate('/admin-page');
      } else {
        navigate('/user-page');
      }
      return null;
    }
  }

  return (
    <div className="App">
      <Header 
        onLogout={handleLogout} 
        userType={userType} 
        onSentEmailsClick={userType === 'admin' ? handleSentEmailsClick : undefined}
        onNotificationsClick={handleNotificationsClick}
        onNewRequestClick={userType === 'user' ? handleNewRequestClick : undefined}
      />
      <main className="main-content">
        <div className="dashboard-container">
          {currentView === 'sent-emails' ? (
            <div>
              <div className="back-button-container">
                <button onClick={handleBackToMain} className="back-btn">
                  {t('common.back_to_dashboard')}
                </button>
              </div>
              <SentEmailsDashboard />
            </div>
          ) : (
            <>
              {userType === 'admin' ? (
                <AdminTable />
              ) : (
                <>
                  <StatsCards />
                  <div className="charts-section">
                    <YearlyOverview />
                    <SavingsChart />
                  </div>
                  <ProcurementSection />
                </>
              )}
            </>
          )}
        </div>
      </main>
      
      {/* Notification Modal */}
      <NotificationModal 
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        onRevisionClick={handleRevisionClick}
      />
      
      {/* Revision Modal */}
      <NewRequestModal
        isOpen={showRevisionModal}
        onClose={handleRevisionModalClose}
        onSubmit={handleRevisionSubmit}
        editingRequest={revisionRequestData}
        isSubmitting={false}
      />
      
      {/* New Request Modal (for header button) */}
      <NewRequestModal
        isOpen={showNewRequestModal}
        onClose={handleCloseNewRequestModal}
        onSubmit={handleSubmitNewRequest}
        editingRequest={null}
        isSubmitting={false}
      />
      
      {/* Token Expired Modal */}
      <TokenExpiredModal
        isOpen={showTokenExpiredModal}
        onClose={() => setShowTokenExpiredModal(false)}
        onLogin={handleTokenExpiredLogin}
      />
      
      {/* Report Issue Button - Floating button (only for non-admin users) */}
      {userType !== 'admin' && <ReportIssueButton />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <TranslationProvider>
        <AuthProvider>
          <CurrencyProvider>
            <Routes>
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/general-terms" element={<GeneralTerms />} />
              <Route path="/signin" element={<AppContent />} />
              <Route path="/user-page" element={<AppContent />} />
              <Route path="/admin-page" element={<AppContent />} />
              <Route path="/invoice" element={<InvoicePage />} />
              <Route path="/invoice/verificate/:id" element={<InvoiceVerificationPage />} />
              <Route path="/admin/request/:requestId" element={<RequestDetailPage />} />
              <Route path="/" element={<AppContent />} />
              <Route path="*" element={<AppContent />} />
            </Routes>
          </CurrencyProvider>
        </AuthProvider>
      </TranslationProvider>
    </Router>
  );
}

export default App;
